import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import configWeb from "../../../config.js/configWeb";
import { simpleGetCall } from "../../../config.js/SetUp";
import "./PromoTicker.css";

const PromoTicker = () => {
  // Preserve the requested locale; translate the API's known EN text through
  // the existing locale dictionary (the public endpoint accepts fr).
  const { t } = useTranslation();
  const [tickers, setTickers] = useState([]);
  const [scrollSpeed, setScrollSpeed] = useState(30); // default 30 seconds
  const language = useSelector((state) => state.language.language);

  useEffect(() => {
    const scoped = process.env.REACT_APP_SCOPED_SPEED === "true";
    let active = true;
    const controller = new AbortController();
    let timer;
    const fetchTickers = async () => {
      try {
        const lang = ["en", "fr", "ar"].includes(language) ? language : "en";
        const url = `${configWeb.GET_PROMO_TICKER}?lang=${lang}`;
        // Was a bare fetch() sending `x-api-key: process.env.REACT_APP_API_KEY`.
        // That variable is not defined at build time (no .env ships with the
        // frontend), so the header went out as the literal string "undefined"
        // and the API correctly answered 401 on every page load — the
        // unexplained first-party console error.
        //
        // Every other caller in the app goes through simpleGetCall, which sends
        // `REACT_APP_API_KEY || DEFAULT_API_KEY`. Reusing it fixes the auth for
        // real and keeps the credential in exactly one place instead of
        // duplicating it here.
        const res = await simpleGetCall(url, scoped ? { signal: controller.signal } : undefined);
        if (!active) return;
        // Handle both direct array and wrapped { data: [...] } responses
        const data = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        setTickers(data);
        // Read scroll_speed from API response (seconds). Supports:
        // - Top-level: { scroll_speed: 25, data: [...] }
        // - Per-ticker: [{ scroll_speed: 25, text: "..." }]
        const speed = res?.scroll_speed || data?.[0]?.scroll_speed;
        if (speed && Number(speed) > 0) {
          setScrollSpeed(Number(speed));
        }
      } catch (err) {
        if (!scoped) console.error("Failed to fetch promo ticker:", err);
        if (active) setTickers([]);
      }
    };

    const start = () => { timer = setTimeout(() => {
      const timeout = setTimeout(() => controller.abort(), 2500);
      fetchTickers().finally(() => clearTimeout(timeout));
    }, 1500); };
    if (!scoped) fetchTickers();
    else if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => { active = false; clearTimeout(timer); controller.abort(); window.removeEventListener("load", start); };
  }, [language]);

  if (tickers.length === 0) return null;

  // Bulletproof external link handler — prevents any default browser/router
  // navigation and uses window.open to guarantee the full external URL opens
  const handleExternalLink = (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    // Ensure the URL has a protocol prefix
    const finalUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  // Convert URLs in text to clickable <span> elements (NOT <a> tags)
  // Using <span> avoids any browser href-based navigation entirely
  const renderTextWithLinks = (text) => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((part, i) =>
      /^https?:\/\//.test(part) ? (
        <span
          key={i}
          role="link"
          tabIndex={0}
          className="promo-ticker-link"
          style={{ cursor: "pointer" }}
          onClick={(e) => handleExternalLink(e, part)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleExternalLink(e, part);
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="promo-ticker-bar">
      <div className="promo-ticker-content">
        <div
          className="promo-ticker-scroll"
          style={{ animationDuration: `${scrollSpeed}s` }}
        >
          {tickers.map((ticker, index) => (
            <span key={ticker.id} className="promo-ticker-item">
              {ticker.link ? (
                <span
                  role="link"
                  tabIndex={0}
                  className="promo-ticker-link"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => handleExternalLink(e, ticker.link)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleExternalLink(e, ticker.link);
                  }}
                >
                  {t(ticker.text)}
                </span>
              ) : (
                renderTextWithLinks(t(ticker.text))
              )}
              {tickers.length > 1 && index < tickers.length - 1 && (
                <span className="promo-ticker-separator">|</span>
              )}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PromoTicker;
